import { useEffect, useState, Fragment } from "react";
import { Link, NavLink, Route, Routes, Navigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useContent } from "../contexts/ContentContext";
import ImageUpload, { fileUrl } from "../components/ImageUpload";
import AdminTicketing from "./AdminTicketing";
import AdminArtists from "./AdminArtists";
import AdminMedia from "./AdminMedia";
import { Loader2, Save, RotateCcw, CheckCircle2, XCircle, Eye, ShieldCheck, FileText, Users, Image as ImageIcon, BarChart3, Ban, RefreshCw, FileSignature, Download, Music, Ticket, Trash2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function imgSrc(v) {
  if (!v) return null;
  if (v.startsWith("http") || v.startsWith("/")) return v;
  return `${BACKEND}/api/files/${v}`;
}

export default function AdminApp() {
  const { user } = useAuth();
  if (user === undefined) return <div className="container-mc py-20 text-center font-mono text-sm uppercase tracking-wider text-muted-foreground">Chargement…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/login" replace />;

  return (
    <div className="container-mc py-8" data-testid="admin-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="overline mb-1">Console MCZ EVENTS</p>
          <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter">Administration</h1>
        </div>
      </div>

      <nav className="flex gap-1 border-b border-border mb-8 overflow-x-auto">
        {[
          { to: "/admin", label: "Vue d'ensemble", Icon: BarChart3, end: true, testid: "admin-tab-stats" },
          { to: "/admin/kyc", label: "Modération KYC", Icon: ShieldCheck, testid: "admin-tab-kyc" },
          { to: "/admin/users", label: "Utilisateurs", Icon: Users, testid: "admin-tab-users" },
          { to: "/admin/artists", label: "Artistes", Icon: Music, testid: "admin-tab-artists" },
          { to: "/admin/media", label: "Médias", Icon: ImageIcon, testid: "admin-tab-media" },
          { to: "/admin/ticketing", label: "Billetterie", Icon: Ticket, testid: "admin-tab-ticketing" },
          { to: "/admin/contracts", label: "Contrats", Icon: FileSignature, testid: "admin-tab-contracts" },
          { to: "/admin/exports", label: "Exports", Icon: Download, testid: "admin-tab-exports" },
          { to: "/admin/content", label: "Contenu du site", Icon: FileText, testid: "admin-tab-content" },
        ].map(({ to, label, Icon, end, testid }) => (
          <NavLink key={to} to={to} end={end} data-testid={testid}
            className={({ isActive }) => `px-4 py-3 flex items-center gap-2 text-sm font-bold whitespace-nowrap border-b-2 transition-colors ${isActive ? "border-foreground text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="w-4 h-4" /> {label}
          </NavLink>
        ))}
      </nav>

      <Routes>
        <Route index element={<AdminStats />} />
        <Route path="kyc" element={<AdminKyc />} />
        <Route path="users" element={<AdminUsers />} />
        <Route path="artists" element={<AdminArtists />} />
        <Route path="media" element={<AdminMedia />} />
        <Route path="ticketing" element={<AdminTicketing />} />
        <Route path="contracts" element={<AdminContracts />} />
        <Route path="exports" element={<AdminExports />} />
        <Route path="content" element={<AdminContent />} />
      </Routes>
    </div>
  );
}

function AdminStats() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)); }, []);
  if (!stats) return <Loading />;
  const cards = [
    { label: "KYC en attente", value: stats.pending_kyc, accent: "text-[hsl(var(--secondary))]", link: "/admin/kyc" },
    { label: "KYC approuvés", value: stats.approved_kyc, accent: "text-[hsl(var(--accent))]" },
    { label: "KYC rejetés", value: stats.rejected_kyc, accent: "text-foreground" },
    { label: "Utilisateurs", value: stats.total_users, accent: "text-foreground" },
    { label: "Comptes bannis", value: stats.banned_users ?? 0, accent: "text-[hsl(var(--primary))]", link: "/admin/users" },
    { label: "Artistes publiés", value: stats.total_artists, accent: "text-foreground" },
    { label: "Sollicitations", value: stats.total_bookings, accent: "text-foreground" },
    { label: "Contrats actifs", value: stats.open_contracts, accent: "text-[hsl(var(--secondary))] mix-blend-multiply", link: "/admin/contracts" },
    { label: "Contrats clôturés", value: stats.closed_contracts, accent: "text-[hsl(var(--accent))]", link: "/admin/contracts" },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-0 card-mc" data-testid="admin-stats-grid">
      {cards.map((c, i) => (
        <div key={i} className="p-6 border-b border-r border-border">
          <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">{c.label}</p>
          <p className={`mt-2 font-display font-black text-4xl tracking-tighter ${c.accent}`}>{c.value}</p>
          {c.link && <Link to={c.link} className="text-xs font-bold underline mt-2 inline-block">Voir →</Link>}
        </div>
      ))}
    </div>
  );
}

function AdminKyc() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/users?kyc_status=submitted");
    setUsers(data.users || []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const moderate = async (uid, action) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${uid}/kyc`, { action, reason: action === "reject" ? reason : "" });
      toast.success(action === "approve" ? "KYC approuvé" : "KYC rejeté");
      setReviewing(null);
      setReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (loading) return <Loading />;
  if (users.length === 0) return <EmptyState text="Aucun KYC en attente de modération." />;

  return (
    <div className="space-y-4" data-testid="admin-kyc-list">
      {users.map((u) => (
        <div key={u.id} className="card-mc p-5" data-testid={`kyc-row-${u.id}`}>
          <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
            <div className="md:col-span-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{new Date(u.kyc_submitted_at || u.created_at).toLocaleString("fr-FR")}</p>
              <p className="font-display font-black text-lg">{u.name}</p>
              <p className="text-xs text-muted-foreground">{u.email}</p>
              <p className="text-xs font-mono">{u.phone}</p>
              <span className="status-badge mt-2 bg-muted">{u.role}</span>
            </div>

            <div className="md:col-span-4">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Pièce ({u.kyc_id_document_type || "—"})</p>
              {u.kyc_id_document_file_id ? (
                <a href={imgSrc(u.kyc_id_document_file_id)} target="_blank" rel="noreferrer" data-testid={`kyc-id-${u.id}`} className="block aspect-[4/3] bg-muted card-mc overflow-hidden">
                  <img src={imgSrc(u.kyc_id_document_file_id)} alt="ID" className="w-full h-full object-cover" />
                </a>
              ) : <p className="text-xs italic text-muted-foreground">Manquante</p>}
            </div>
            <div className="md:col-span-3">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Selfie</p>
              {u.kyc_selfie_file_id ? (
                <a href={imgSrc(u.kyc_selfie_file_id)} target="_blank" rel="noreferrer" data-testid={`kyc-selfie-${u.id}`} className="block aspect-square bg-muted card-mc overflow-hidden">
                  <img src={imgSrc(u.kyc_selfie_file_id)} alt="Selfie" className="w-full h-full object-cover" />
                </a>
              ) : <p className="text-xs italic text-muted-foreground">Manquant</p>}
            </div>

            <div className="md:col-span-2 flex flex-col gap-2">
              <button data-testid={`approve-${u.id}`} disabled={busy} onClick={() => moderate(u.id, "approve")} className="btn-secondary text-xs px-3 py-2 inline-flex items-center justify-center gap-1">
                <CheckCircle2 className="w-3.5 h-3.5" /> Approuver
              </button>
              <button data-testid={`reject-trigger-${u.id}`} disabled={busy} onClick={() => { setReviewing(u); setReason(""); }} className="btn-outline text-xs px-3 py-2 inline-flex items-center justify-center gap-1">
                <XCircle className="w-3.5 h-3.5" /> Rejeter
              </button>
            </div>
          </div>

          {reviewing?.id === u.id && (
            <div className="mt-4 border-t border-border pt-4" data-testid={`reject-form-${u.id}`}>
              <label className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground">Motif du rejet</label>
              <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="w-full mt-1 border border-border px-3 py-2 rounded-sm bg-card text-sm" placeholder="Pièce illisible, selfie non conforme..." />
              <div className="flex gap-2 mt-2">
                <button onClick={() => setReviewing(null)} className="btn-ghost text-xs">Annuler</button>
                <button data-testid={`reject-confirm-${u.id}`} disabled={busy} onClick={() => moderate(u.id, "reject")} className="btn-primary text-xs">Confirmer le rejet</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ q: "", role: "", kyc_status: "" });
  const [banning, setBanning] = useState(null);
  const [banReason, setBanReason] = useState("");
  const [deleting, setDeleting] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (filter.q) p.set("q", filter.q);
    if (filter.role) p.set("role", filter.role);
    if (filter.kyc_status) p.set("kyc_status", filter.kyc_status);
    const { data } = await api.get(`/admin/users?${p}`);
    setUsers(data.users || []);
    setLoading(false);
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, [filter.role, filter.kyc_status]);

  const submitBan = async (uid, action) => {
    setBusy(true);
    try {
      await api.patch(`/admin/users/${uid}/ban`, { action, reason: action === "ban" ? banReason : "" });
      toast.success(action === "ban" ? "Utilisateur banni" : "Bannissement levé");
      setBanning(null); setBanReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitDelete = async (uid) => {
    setBusy(true);
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("Compte supprimé définitivement");
      setDeleting(null);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-users-page">
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="card-mc p-4 mb-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <input data-testid="users-search" value={filter.q} onChange={(e) => setFilter({...filter, q: e.target.value})} placeholder="Recherche nom/email/téléphone" className="border border-border px-3 py-2 rounded-sm bg-card text-sm md:col-span-2" />
        <select data-testid="users-role-filter" value={filter.role} onChange={(e) => setFilter({...filter, role: e.target.value})} className="border border-border px-3 py-2 rounded-sm bg-card text-sm">
          <option value="">Tous rôles</option>
          <option value="client">Client</option>
          <option value="artist">Artiste</option>
          <option value="admin">Admin</option>
        </select>
        <select data-testid="users-kyc-filter" value={filter.kyc_status} onChange={(e) => setFilter({...filter, kyc_status: e.target.value})} className="border border-border px-3 py-2 rounded-sm bg-card text-sm">
          <option value="">Tous KYC</option>
          <option value="submitted">Soumis</option>
          <option value="approved">Approuvé</option>
          <option value="rejected">Rejeté</option>
        </select>
      </form>

      {loading ? <Loading /> : (
        <div className="card-mc overflow-x-auto" data-testid="users-table">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                {["Nom", "Email", "Téléphone", "Rôle", "KYC", "Statut", "Inscription", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <Fragment key={u.id}>
                <tr className="border-t border-border" data-testid={`user-row-${u.id}`}>
                  <td className="px-4 py-3 font-bold">{u.name}</td>
                  <td className="px-4 py-3 font-mono text-xs">{u.email}</td>
                  <td className="px-4 py-3 font-mono text-xs">{u.phone}</td>
                  <td className="px-4 py-3"><span className="status-badge bg-muted">{u.role}</span></td>
                  <td className="px-4 py-3"><span className={`status-badge ${u.kyc_status === "approved" ? "bg-[hsl(var(--accent))] text-white" : u.kyc_status === "rejected" ? "bg-destructive text-white" : u.kyc_status === "submitted" ? "bg-[hsl(var(--secondary))]" : "bg-muted"}`}>{u.kyc_status || "—"}</span></td>
                  <td className="px-4 py-3">
                    {u.is_banned ? <span className="status-badge bg-destructive text-white">BANNI</span> : <span className="status-badge bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))]">actif</span>}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{new Date(u.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="px-4 py-3">
                    {u.role !== "admin" && (
                      <div className="flex gap-1.5">
                        {u.is_banned ? (
                          <button data-testid={`unban-${u.id}`} disabled={busy} onClick={() => submitBan(u.id, "unban")} className="btn-secondary text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1">
                            <RefreshCw className="w-3 h-3" /> Lever
                          </button>
                        ) : (
                          <button data-testid={`ban-trigger-${u.id}`} disabled={busy} onClick={() => { setBanning(u); setDeleting(null); setBanReason(""); }} className="btn-outline text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1">
                            <Ban className="w-3 h-3" /> Bannir
                          </button>
                        )}
                        <button data-testid={`delete-trigger-${u.id}`} disabled={busy} onClick={() => { setDeleting(u); setBanning(null); }} title="Supprimer définitivement" className="btn-outline text-[11px] px-2.5 py-1.5 inline-flex items-center gap-1 text-destructive border-destructive">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                {deleting?.id === u.id && (
                  <tr data-testid={`delete-form-${u.id}`}>
                    <td colSpan={8} className="px-4 py-4 bg-destructive/5 border-t border-border">
                      <p className="text-sm font-bold text-destructive">Supprimer définitivement le compte de {u.name} ({u.email}) ?</p>
                      <p className="text-xs text-muted-foreground mt-1">Cette action est irréversible : profil{u.role === "artist" ? " artiste, événements sans billets vendus" : ""}, conversations et messages seront supprimés. Les contrats, paiements et billets déjà vendus sont conservés pour l'historique comptable.</p>
                      <div className="flex gap-2 mt-3 justify-end">
                        <button data-testid={`cancel-delete-${u.id}`} onClick={() => setDeleting(null)} className="btn-ghost text-xs">Annuler</button>
                        <button data-testid={`delete-confirm-${u.id}`} disabled={busy} onClick={() => submitDelete(u.id)} className="btn-primary text-xs bg-destructive">
                          {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Trash2 className="w-3 h-3 mr-1" />} Confirmer la suppression
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
                {banning?.id === u.id && (
                  <tr data-testid={`ban-form-${u.id}`}>
                    <td colSpan={8} className="px-4 py-4 bg-muted border-t border-border">
                      <p className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground mb-1">Motif du bannissement de <strong>{u.name}</strong></p>
                      <textarea value={banReason} onChange={(e) => setBanReason(e.target.value)} rows={2} className="w-full border border-border px-3 py-2 rounded-sm bg-card text-sm" placeholder="Fraude détectée, faux profil, etc." />
                      <div className="flex gap-2 mt-2 justify-end">
                        <button onClick={() => setBanning(null)} className="btn-ghost text-xs">Annuler</button>
                        <button data-testid={`ban-confirm-${u.id}`} disabled={busy} onClick={() => submitBan(u.id, "ban")} className="btn-primary text-xs"><Ban className="w-3 h-3 mr-1" />Confirmer le bannissement</button>
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              ))}
              {users.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-muted-foreground">Aucun résultat</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminContracts() {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");

  const load = async () => {
    setLoading(true);
    const p = new URLSearchParams();
    if (status) p.set("status", status);
    const { data } = await api.get(`/admin/contracts?${p}`);
    setContracts(data.contracts || []);
    setLoading(false);
  };
  // eslint-disable-next-line
  useEffect(() => { load(); }, [status]);

  return (
    <div data-testid="admin-contracts-page">
      <div className="card-mc p-4 mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <p className="text-sm font-bold">{contracts.length} contrat(s)</p>
        <select data-testid="contracts-status-filter" value={status} onChange={(e) => setStatus(e.target.value)} className="border border-border px-3 py-2 rounded-sm bg-card text-sm md:ml-auto">
          <option value="">Tous statuts</option>
          <option value="open">Ouvert</option>
          <option value="in_progress">En cours</option>
          <option value="validated">Validé</option>
          <option value="closed">Clôturé</option>
          <option value="cancelled">Annulé</option>
        </select>
      </div>

      {loading ? <Loading /> : (
        <div className="card-mc overflow-x-auto" data-testid="contracts-table">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr>
                {["Date", "Client", "Artiste", "Événement", "Montant", "Commission", "Statut"].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contracts.map((c) => (
                <tr key={c.id} className="border-t border-border" data-testid={`contract-row-${c.id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{new Date(c.created_at).toLocaleDateString("fr-FR")}</td>
                  <td className="px-4 py-3">{c.client_name}<br /><span className="text-[10px] text-muted-foreground font-mono">{c.client_email}</span></td>
                  <td className="px-4 py-3 font-bold">{c.artist_stage_name}<br /><span className="text-[10px] text-muted-foreground font-mono">{c.artist_category}</span></td>
                  <td className="px-4 py-3 text-xs">{c.booking?.event_type || "—"}<br /><span className="font-mono">{c.booking?.event_date || ""}</span></td>
                  <td className="px-4 py-3 font-mono text-xs">{c.agreed_amount_gnf ? `${new Intl.NumberFormat("fr-FR").format(c.agreed_amount_gnf)} GNF` : "—"}</td>
                  <td className="px-4 py-3 font-mono text-xs">{c.commission_gnf ? `${new Intl.NumberFormat("fr-FR").format(c.commission_gnf)} GNF` : "—"}</td>
                  <td className="px-4 py-3"><span className={`status-badge ${c.status === "closed" ? "bg-foreground text-background" : c.status === "validated" ? "bg-[hsl(var(--accent))] text-white" : c.status === "cancelled" ? "bg-muted" : "bg-[hsl(var(--secondary))]"}`}>{c.status}</span></td>
                </tr>
              ))}
              {contracts.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-muted-foreground">Aucun contrat.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AdminExports() {
  const [downloading, setDownloading] = useState(null);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const download = async (path, filename) => {
    setDownloading(path);
    try {
      const token = localStorage.getItem("mcevents_token");
      const r = await fetch(`${BACKEND_URL}/api${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!r.ok) throw new Error("Échec du téléchargement");
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); URL.revokeObjectURL(url);
      toast.success("Téléchargement lancé");
    } catch (e) { toast.error(e.message); }
    finally { setDownloading(null); }
  };

  const cards = [
    { key: "users-csv", title: "Utilisateurs · CSV", desc: "Tableur (Excel, Numbers, Google Sheets)", path: "/admin/export/users?fmt=csv", filename: "mcz-utilisateurs.csv" },
    { key: "users-pdf", title: "Utilisateurs · PDF", desc: "Document imprimable avec en-tête MCZ EVENTS", path: "/admin/export/users?fmt=pdf", filename: "mcz-utilisateurs.pdf" },
    { key: "contracts-csv", title: "Contrats · CSV", desc: "Tableur avec tous les contrats et montants", path: "/admin/export/contracts?fmt=csv", filename: "mcz-contrats.csv" },
    { key: "contracts-pdf", title: "Contrats · PDF", desc: "Rapport PDF des contrats avec montants & commissions", path: "/admin/export/contracts?fmt=pdf", filename: "mcz-contrats.pdf" },
  ];

  return (
    <div data-testid="admin-exports-page">
      <div className="card-mc p-5 bg-[hsl(var(--muted))]/40 mb-6">
        <p className="overline mb-2">Exports comptables & légaux</p>
        <p className="text-sm">Téléchargez instantanément la liste de vos utilisateurs ou contrats au format CSV (tableur) ou PDF (impression).</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cards.map((c) => (
          <div key={c.key} className="card-mc p-5" data-testid={`export-card-${c.key}`}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 bg-[hsl(var(--primary))] text-white flex items-center justify-center rounded-sm">
                <Download className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-display font-black text-lg">{c.title}</p>
                <p className="text-xs text-muted-foreground">{c.desc}</p>
              </div>
            </div>
            <button data-testid={`download-${c.key}`} disabled={downloading === c.path} onClick={() => download(c.path, c.filename)} className="btn-primary w-full mt-4">
              {downloading === c.path ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Télécharger
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function AdminContent() {
  const { refresh: refreshContent } = useContent();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingKey, setSavingKey] = useState(null);
  const [drafts, setDrafts] = useState({});

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/content");
    setPages(data.pages || []);
    const d = {};
    for (const p of data.pages || []) for (const it of p.items) d[it.key] = it.value;
    setDrafts(d);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (key) => {
    setSavingKey(key);
    try {
      await api.patch(`/admin/content/${encodeURIComponent(key)}`, { value: drafts[key] ?? "" });
      toast.success("Sauvegardé");
      await load();
      await refreshContent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(null); }
  };

  const reset = async (key) => {
    setSavingKey(key);
    try {
      const { data } = await api.delete(`/admin/content/${encodeURIComponent(key)}`);
      setDrafts((d) => ({ ...d, [key]: data.value }));
      toast.success("Réinitialisé à la valeur par défaut");
      await load();
      await refreshContent();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingKey(null); }
  };

  if (loading) return <Loading />;
  return (
    <div className="space-y-8" data-testid="admin-content-page">
      <div className="card-mc p-5 bg-[hsl(var(--muted))]/40">
        <p className="overline mb-2">CMS · Édition du site</p>
        <p className="text-sm">Modifiez ici tous les textes et images visibles publiquement sur le site. Les modifications sont visibles instantanément après sauvegarde.</p>
      </div>

      {pages.map((page) => (
        <section key={page.page} data-testid={`content-page-${page.page}`}>
          <h2 className="font-display font-black text-2xl tracking-tighter mb-4">{page.page}</h2>
          <div className="grid grid-cols-1 gap-4">
            {page.items.map((it) => {
              const v = drafts[it.key] ?? "";
              const dirty = v !== it.value;
              const isImage = it.type === "image";
              return (
                <div key={it.key} className="card-mc p-5" data-testid={`content-${it.key}`}>
                  <div className="flex items-start justify-between mb-3 gap-3">
                    <div>
                      <p className="font-display font-bold">{it.label}</p>
                      <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mt-0.5">{it.key} · {it.type}</p>
                      {it.is_overridden && <p className="text-[10px] font-mono text-[hsl(var(--accent))] mt-1">Modifié{it.updated_by ? ` par ${it.updated_by}` : ""}</p>}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      {it.is_overridden && (
                        <button data-testid={`reset-${it.key}`} disabled={savingKey === it.key} onClick={() => reset(it.key)} className="btn-ghost text-xs">
                          <RotateCcw className="w-3 h-3 mr-1" /> Défaut
                        </button>
                      )}
                      <button data-testid={`save-${it.key}`} disabled={!dirty || savingKey === it.key} onClick={() => save(it.key)} className="btn-primary text-xs">
                        {savingKey === it.key ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Save className="w-3 h-3 mr-1" />} Enregistrer
                      </button>
                    </div>
                  </div>

                  {isImage ? (
                    <ImageUpload value={v} onChange={(val) => setDrafts((d) => ({ ...d, [it.key]: val }))} testid={`image-${it.key}`} label="" aspect="aspect-[4/3]" />
                  ) : it.type === "textarea" ? (
                    <textarea data-testid={`input-${it.key}`} value={v} onChange={(e) => setDrafts((d) => ({ ...d, [it.key]: e.target.value }))} rows={3} className="w-full border border-border px-3 py-2 rounded-sm bg-card text-sm" />
                  ) : (
                    <input data-testid={`input-${it.key}`} value={v} onChange={(e) => setDrafts((d) => ({ ...d, [it.key]: e.target.value }))} className="w-full border border-border px-3 py-2 rounded-sm bg-card text-sm" />
                  )}
                </div>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

function Loading() {
  return <div className="text-center font-mono text-sm uppercase tracking-wider text-muted-foreground py-12">Chargement…</div>;
}

function EmptyState({ text }) {
  return <div className="card-mc p-12 text-center text-muted-foreground">{text}</div>;
}
